// Ctrl/⌘+K: jump to any page or record, or run an action. Searches local data through our own index (lib/search.ts), so
// it's instant: items by SKU, barcode, vendor SKU or name; POs (any status, the vendor's ref); runs; bills; BOMs;
// receipts; vendors; projects. An exact code or a scanned barcode comes first, so Enter (a scanner's last key) opens it.
import { Command } from "cmdk";
import { Boxes, ClipboardList, FolderKanban, Hammer, Layers, LogOut, Package, PackageCheck, Palette, Plus, Receipt, ScanLine, Truck, User } from "lucide-react";
import { type ReactNode, useEffect, useMemo, useState } from "react";
import { BILL_STATUS_LABELS } from "../../../shared/bills.ts";
import { axesText } from "../../../shared/part-groups.ts"; // Part groups ---
import { PO_STATUS_LABELS } from "../../../shared/po.ts";
import { projectPath } from "../../../shared/projects.ts"; // Manufacturing projects ---
import { canReceive } from "../../../shared/receiving.ts";
import { RUN_STATUS_LABELS } from "../../../shared/runs.ts";
import { canSee } from "../lib/access.ts";
import { bomPath } from "../lib/boms.ts";
import { can } from "../lib/mutate.ts";
import { navigate } from "../lib/router.tsx";
import { buildIndex, type EntryInput, type EntryKind, type Hit, search } from "../lib/search.ts";
import { me, signOut } from "../lib/session.ts";
import { useRows } from "../lib/sync.ts";
import { isListed } from "../nav.ts";
import { routes } from "../routes.tsx";
import { nameParts } from "../ui/controls.tsx";
import { cycleTheme } from "./theme.ts";

let setOpenGlobal: ((open: boolean) => void) | null = null;
export const openPalette = () => setOpenGlobal?.(true);

export function CommandPalette() {
  const [open, setOpen] = useState(false);
  setOpenGlobal = setOpen;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key.toLowerCase() === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <Command.Dialog
      open={open}
      onOpenChange={setOpen}
      label="Jump to"
      shouldFilter={false}
      overlayClassName="fixed inset-0 z-40 bg-black/30"
      contentClassName="fixed top-[14vh] left-1/2 z-50 w-[620px] max-w-[calc(100vw-32px)] -translate-x-1/2 overflow-hidden rounded-xl border border-line bg-surface shadow-pop"
    >
      {open && <PaletteBody close={() => setOpen(false)} />}
    </Command.Dialog>
  );
}

const ICONS: Record<EntryKind, ReactNode> = {
  page: null,
  action: <Plus className="size-4" />,
  item: <Package className="size-4" />,
  group: <Boxes className="size-4" />,
  po: <ClipboardList className="size-4" />,
  run: <Hammer className="size-4" />,
  bill: <Receipt className="size-4" />,
  bom: <Layers className="size-4" />,
  receipt: <PackageCheck className="size-4" />,
  vendor: <Truck className="size-4" />,
  project: <FolderKanban className="size-4" />,
  user: <User className="size-4" />,
};
const HEADINGS: Record<EntryKind, string> = {
  page: "Pages",
  action: "Actions",
  item: "Items",
  group: "Part groups",
  po: "Purchase orders",
  run: "Runs",
  bill: "Bills",
  bom: "BOMs",
  receipt: "Receipts",
  vendor: "Vendors",
  project: "Projects",
  user: "Users",
};

/** Everything the palette can find, for this person's role. */
function useEntries(): { entries: EntryInput[]; pageIcons: Map<string, ReactNode>; receivable: Map<string, string> } {
  const role = me()?.role;
  const vendors = useRows("vendor");
  const projects = useRows("project");
  const users = useRows("app_user");
  const items = useRows("item");
  const partGroups = useRows("part_group"); // Part groups ---
  const vendorItems = useRows("vendor_item");
  const pos = useRows("po");
  const runs = useRows("production_run");
  const bills = useRows("bill");
  const boms = useRows("bom");
  const receipts = useRows("receipt");
  return useMemo(() => {
    const out: EntryInput[] = [];
    const pageIcons = new Map<string, ReactNode>();
    for (const r of routes.filter((r) => isListed(r) && canSee(r, role))) {
      pageIcons.set(r.path, <r.icon className="size-4" />);
      out.push({ kind: "page", id: r.path, title: r.label, hint: r.section, to: r.path, words: "page" });
    }
    const vendorName = new Map(vendors.map((v) => [v.id, v.short_name || v.name]));
    const receivable = new Map(pos.filter((p) => canReceive(p.status) && p.status !== "received").map((p) => [p.id, p.number]));
    const actions: [boolean, string, string, string][] = [
      [can("po.create"), "New PO", "/pos?new=1", "purchase order create"],
      [can("bill.create"), "New bill", "/bills?new=1", "invoice create"],
      [can("vendor.create"), "New vendor", "/vendors?new=1", "create supplier"],
      [can("project.create"), "New project", "/projects?new=1", "create"],
      [true, "Switch theme", "!theme", "dark light"],
      [true, "Sign out", "!signout", "log out"],
    ];
    for (const [ok, title, to, words] of actions) if (ok) out.push({ kind: "action", id: to, title, to, words: `action ${words}` });
    // Receivers find the POs they can count against, and nothing else.
    if (role === "receiving") {
      for (const p of pos) {
        if (!receivable.has(p.id)) continue;
        out.push({
          kind: "po",
          id: p.id,
          title: `${p.number} ${vendorName.get(p.vendor_id) ?? ""}`,
          hint: PO_STATUS_LABELS[p.status],
          to: `/receiving?po=${p.id}`,
          codes: [p.number, p.vendor_ref],
        });
      }
      return { entries: out, pageIcons, receivable };
    }

    const vendorSkus = new Map<string, string[]>();
    for (const l of vendorItems) {
      const list = vendorSkus.get(l.item_id) ?? [];
      list.push(l.vendor_sku, l.vendor_name);
      vendorSkus.set(l.item_id, list);
    }
    for (const i of items) {
      const { first, rest } = nameParts(i.name, i);
      const theirs = vendorSkus.get(i.id) ?? [];
      out.push({
        kind: "item",
        id: i.id,
        title: rest ? `${first} · ${rest}` : first,
        hint: [i.sku, i.archived ? "archived" : ""].filter(Boolean).join(" · "),
        to: `${i.source === "shopify" ? "/items/sell" : "/parts"}?open=${i.id}`,
        codes: [i.sku, i.barcode, ...theirs.filter((_, k) => k % 2 === 0)],
        words: [i.name, ...theirs].join(" "),
      });
    }
    // Part groups --- by name and SKU prefix, opening the Parts library on the group's parts
    const inGroup = new Map<string, number>();
    for (const i of items) if (i.part_group_id && !i.archived) inGroup.set(i.part_group_id, (inGroup.get(i.part_group_id) ?? 0) + 1);
    for (const g of partGroups)
      out.push({
        kind: "group",
        id: g.id,
        title: g.name,
        hint: `${inGroup.get(g.id) ?? 0} parts · ${axesText(g)}`,
        to: `/parts?group=${g.id}`,
        codes: [g.sku_prefix],
        words: `part group ${g.varies_by.join(" ")}`,
      });
    for (const p of pos) {
      const vendor = vendorName.get(p.vendor_id) ?? "";
      out.push({
        kind: "po",
        id: p.id,
        title: `${p.number} ${vendor}`,
        hint: [p.vendor_ref, PO_STATUS_LABELS[p.status]].filter(Boolean).join(" · "),
        to: `/pos?open=${p.id}`,
        codes: [p.number, p.vendor_ref],
      });
    }
    const projectById = new Map(projects.map((p) => [p.id, p]));
    for (const r of runs) {
      const project = projectById.get(r.project_id);
      if (!project) continue;
      out.push({
        kind: "run",
        id: r.id,
        title: `${r.number} ${r.name}`,
        hint: `${project.code || project.name} · ${RUN_STATUS_LABELS[r.status]}`,
        to: `${projectPath(project, "productions")}&run=${encodeURIComponent(r.number)}`,
        codes: [r.number, r.external_ref],
        words: project.name,
      });
    }
    for (const b of bills) {
      out.push({
        kind: "bill",
        id: b.id,
        title: `${b.number} ${vendorName.get(b.vendor_id) ?? ""}`,
        hint: [b.invoice_number, BILL_STATUS_LABELS[b.status]].filter(Boolean).join(" · "),
        to: `/bills?bill=${b.id}`,
        codes: [b.number, b.invoice_number],
      });
    }
    // A sub-assembly's BOM is its item's; a sell product's (the main ones) is its Shopify product's, found by the
    // product's title or any of its variants' SKUs.
    const itemById = new Map(items.map((i) => [i.id, i]));
    const variantsOf = new Map<string, typeof items>();
    for (const i of items) if (i.shopify_product_id) variantsOf.set(i.shopify_product_id, [...(variantsOf.get(i.shopify_product_id) ?? []), i]);
    for (const b of boms) {
      const item = b.item_id ? itemById.get(b.item_id) : undefined;
      const variants = b.shopify_product_id ? (variantsOf.get(b.shopify_product_id) ?? []) : [];
      const name = item?.name ?? variants[0]?.shopify_product_title;
      if (!name) continue;
      out.push({
        kind: "bom",
        id: b.id,
        title: `${item ? item.sku || item.name : name} rev ${b.revision}`,
        hint: `${item ? `${item.name} · ` : ""}${b.status}`,
        to: bomPath(b.id),
        words: `bom ${name} ${variants.map((v) => v.sku).join(" ")}`,
      });
    }
    const poById = new Map(pos.map((p) => [p.id, p]));
    for (const r of receipts) {
      const po = poById.get(r.po_id);
      out.push({
        kind: "receipt",
        id: r.id,
        title: r.number,
        hint: po ? `${po.number} · ${vendorName.get(po.vendor_id) ?? ""}` : "",
        to: `/pos?open=${r.po_id}&tab=receipts`,
        codes: [r.number],
        words: "receipt received",
      });
    }
    for (const v of vendors) {
      out.push({
        kind: "vendor",
        id: v.id,
        title: v.short_name || v.name,
        hint: v.short_name ? v.name : undefined,
        to: `/vendors?open=${v.id}`,
        words: (v.aliases as string[] | null)?.join(" "),
      });
    }
    for (const p of projects) out.push({ kind: "project", id: p.id, title: p.name, hint: p.code, to: projectPath(p), codes: [p.code] });
    if (role === "admin") {
      for (const u of users) out.push({ kind: "user", id: u.id, title: u.name || u.email, hint: u.email, to: `/settings/users?open=${u.id}` });
    }
    return { entries: out, pageIcons, receivable };
  }, [role, vendors, projects, users, items, partGroups, vendorItems, pos, runs, bills, boms, receipts]);
}

function PaletteBody({ close }: { close: () => void }) {
  const { entries, pageIcons, receivable } = useEntries();
  const index = useMemo(() => buildIndex(entries), [entries]);
  const [query, setQuery] = useState("");
  const hits = useMemo(() => search(index, query), [index, query]);

  const go = (to: string) => {
    close();
    if (to === "!theme") cycleTheme();
    else if (to === "!signout") signOut();
    else navigate(to);
  };
  const icon = (h: { kind: EntryKind; to: string }) =>
    h.kind === "page" ? (
      pageIcons.get(h.to)
    ) : h.to === "!theme" ? (
      <Palette className="size-4" />
    ) : h.to === "!signout" ? (
      <LogOut className="size-4" />
    ) : (
      ICONS[h.kind]
    );
  const row = (h: Hit | EntryInput, key = `${h.kind}:${h.id}`) => (
    <Item key={key} value={key} onSelect={() => go(h.to)} icon={icon(h)} hint={h.hint}>
      {h.title}
    </Item>
  );

  // Nothing typed: the pages and actions. Typed: an exact code first (a scan), "Receive PO-x" for POs that can be
  // counted, then each kind of record, the kind with the best match first.
  const groups = useMemo(() => {
    if (!query.trim()) return null;
    const best = hits.filter((h) => h.rank === 0);
    const rest = hits.filter((h) => h.rank > 0);
    const byKind = new Map<EntryKind, Hit[]>();
    for (const h of rest) byKind.set(h.kind, [...(byKind.get(h.kind) ?? []), h]);
    const receive = hits.filter((h) => h.kind === "po" && receivable.has(h.id) && !h.to.startsWith("/receiving")).slice(0, 2);
    return { best, receive, byKind };
  }, [hits, query, receivable]);

  return (
    <>
      <Command.Input
        autoFocus
        value={query}
        onValueChange={setQuery}
        placeholder="Find a SKU, barcode, PO, run, bill, vendor, page…"
        className="h-12 w-full border-b border-line bg-transparent px-4 text-[14px] placeholder:text-faint focus:outline-none"
      />
      <Command.List className="max-h-[420px] overflow-y-auto p-1.5">
        <Command.Empty className="p-6 text-center text-muted">No results</Command.Empty>
        {!groups ? (
          (["page", "action"] as const).map((kind) => {
            const list = entries.filter((e) => e.kind === kind);
            return list.length ? (
              <Group key={kind} heading={HEADINGS[kind]}>
                {list.map((e) => row(e))}
              </Group>
            ) : null;
          })
        ) : (
          <>
            {groups.best.length > 0 && <Group heading="Exact match">{groups.best.map((h) => row(h))}</Group>}
            {groups.receive.length > 0 && (
              <Group heading="Receive">
                {groups.receive.map((h) => (
                  <Item
                    key={`receive:${h.id}`}
                    value={`receive:${h.id}`}
                    onSelect={() => go(`/receiving?po=${h.id}`)}
                    icon={<PackageCheck className="size-4" />}
                    hint="Count a delivery"
                  >
                    Receive {receivable.get(h.id)}
                  </Item>
                ))}
              </Group>
            )}
            {[...groups.byKind].map(([kind, list]) => (
              <Group key={kind} heading={HEADINGS[kind]}>
                {list.map((h) => row(h))}
              </Group>
            ))}
          </>
        )}
      </Command.List>
      <div className="flex h-8 items-center gap-3 border-t border-line px-3 text-[11px] text-faint">
        <ScanLine className="size-3.5" /> An exact SKU, barcode or PO number comes first: Enter (or a scan) opens it.
      </div>
    </>
  );
}

function Group({ heading, children }: { heading: string; children: ReactNode }) {
  return (
    <Command.Group
      heading={heading}
      className="[&_[cmdk-group-heading]]:px-2.5 [&_[cmdk-group-heading]]:pt-2 [&_[cmdk-group-heading]]:pb-1 [&_[cmdk-group-heading]]:text-[11px] [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:tracking-wide [&_[cmdk-group-heading]]:text-faint [&_[cmdk-group-heading]]:uppercase"
    >
      {children}
    </Command.Group>
  );
}

function Item({ value, onSelect, icon, hint, children }: { value: string; onSelect: () => void; icon: ReactNode; hint?: string; children: ReactNode }) {
  return (
    <Command.Item
      value={value}
      onSelect={onSelect}
      className="flex h-9 cursor-default items-center gap-2.5 rounded-md px-2.5 text-text data-[selected=true]:bg-selected"
    >
      <span className="text-muted">{icon}</span>
      <span className="flex-1 truncate">{children}</span>
      {hint && <span className="max-w-[45%] truncate text-[12px] text-faint">{hint}</span>}
    </Command.Item>
  );
}
